import { Autocomplete, Button, Card, CardContent, CardHeader, Grid, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Iconify from "../../../../minimals/components/iconify";
import { Dispatch, MutableRefObject, useContext, useEffect, useState, SetStateAction } from "react";
import { AddEditContactContext } from "./AddEditContactContext";
import { MuiTelInput, MuiTelInputInfo, matchIsValidTel } from "mui-tel-input";
import isNumber from "../../../utils/isNumber";
import { Contact, Phone, SmartyAddress } from "../../types";
import AddressAutocomplete from "./AddressAutocomplete";
import { usStateList } from "../../../utils/usStateList";
import { countryList } from "../../../utils/countryList";
import axios from "axios";

interface IProps {
  refProp: MutableRefObject<null | HTMLInputElement>;
  theInput: string;
  personalEmailExistsError: boolean;
  setPersonalEmailExistsError: Dispatch<SetStateAction<boolean>>;
  isMerge: boolean;
}
export default function PersonalInformation({
  refProp,
  theInput,
  personalEmailExistsError,
  setPersonalEmailExistsError,
  isMerge
}: IProps) {
  const { handleContactChange, contact, homePhone, setHomePhone, setContact, showError, isValidUSZip } = useContext(AddEditContactContext);

  const [expandPersonalInfo, setExpandPersonalInfo] = useState<boolean>(false);
  const [existingEmailContacts, setExistingEmailContacts] = useState<Contact[]>([]);
  const [originalPersonalEmail, setOriginalPersonalEmail] = useState(contact.personalEmail?.trim() ?? "");

  useEffect(() => {
    const expanded =
      !!contact.preferredName ||
      !!homePhone?.number ||
      !!contact.birthday ||
      !!contact.address1 ||
      !!contact.address2 ||
      !!contact.city ||
      !!contact.state ||
      !!contact.zip;
    setExpandPersonalInfo(expanded);
  }, [contact.id]);

  useEffect(() => {
    if (contact.id) {
      const currentHomePhone = contact.phoneNumbers.find((p) => p.type === "Home") || ({} as Phone);
      setHomePhone(currentHomePhone);
    }
  }, [contact.id]);

  const handlePhoneFields = (info: MuiTelInputInfo) => {
    setHomePhone({
      ...homePhone,
      number: info.nationalNumber || "",
      countryCode: info.countryCallingCode || "",
      type: "Home"
    } as Phone);
  };

  const handleOptionChange = (option: SmartyAddress | null) => {
    setContact((draft) => {
      draft.address1 = option?.formattedStreet_line || "";
      if (option) {
        draft.address2 = option.secondary;
        draft.city = option.city;
        draft.state = option.state;
        draft.zip = option.zipcode;
        draft.country = "United States";
      }
    });
  };
  const checkIfEmailExists = async (email: string) => {
    if (email === originalPersonalEmail) {
      setPersonalEmailExistsError(false);
      return;
    }
    const { data } = await axios.get(`/api/clientphonebook/contacts/checkIfPersonalEmailExists?email=${email}`);
    if (email !== "") {
      setPersonalEmailExistsError(data.emailExists);
      setExistingEmailContacts(data.contacts);
    } else {
      setPersonalEmailExistsError(false);
      setExistingEmailContacts([]);
    }
  };

  useEffect(() => {
    if (contact.personalEmail) {
      setOriginalPersonalEmail(contact.personalEmail.trim());
    }
  }, [contact?.id]);

  return (
    <Card>
      <CardHeader title="Personal Information" />
      <CardContent>
        {!expandPersonalInfo && (
          <Button
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => setExpandPersonalInfo(!expandPersonalInfo)}
          >
            Add Personal Information
          </Button>
        )}
        {expandPersonalInfo && (
          <Grid container columnSpacing={2} rowSpacing={3}>
            <Grid item xs={6}>
              <TextField
                value={contact.preferredName || ""}
                label="Preffered Name"
                fullWidth
                onChange={(e) => handleContactChange("preferredName", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                onChange={(value) => handleContactChange("birthday", value)}
                value={contact.birthday || null}
                slotProps={{ textField: { label: "Birthday" } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                value={contact.personalEmail || ""}
                inputRef={theInput === "Personal Email" ? refProp : null}
                label="Personal Email"
                onChange={(e) => {
                  handleContactChange("personalEmail", e.target.value?.trim());
                }}
                fullWidth
                inputProps={{
                  autoComplete: "new-password"
                }}
                onBlur={(e: any) => {
                  const trimmedEmail = e.target.value.trim();
                  checkIfEmailExists(trimmedEmail);
                }}
              />
            </Grid>
            {personalEmailExistsError && !isMerge && (
              <Typography color={"#ff5252"} marginLeft={2}>
                {existingEmailContacts.map((c) => {
                  return (
                    <div>
                      This email
                      <span style={{ padding: "0 4px" }}>is already in use by phonebook contact</span>
                      <a
                        target="_blank"
                        referrerPolicy="no-referrer"
                        href={`../phonebook/contact/profile?id=${c.id}`}
                        style={{ textDecoration: "none" }}
                      >{`${c.firstName} ${c.lastName}`}</a>
                    </div>
                  );
                })}
              </Typography>
            )}
            <Grid item xs={6}>
              <MuiTelInput
                value={`+${isNumber(homePhone?.countryCode) ? homePhone?.countryCode : 1} ${homePhone?.number}`}
                onChange={(value, info) => handlePhoneFields(info)}
                preferredCountries={["US", "IL"]}
                defaultCountry={"US"}
                forceCallingCode
                label="Home Phone"
                fullWidth
                error={showError}
                helperText={showError ? "Invalid phone number for selected country" : ""}
              />
            </Grid>
            <Grid item xs={8}>
              <AddressAutocomplete
                setAddressOption={handleOptionChange}
                setAddress1={(value) => handleContactChange("address1", value)}
                inputValue={contact?.address1 || ""}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Address 2"
                value={contact?.address2 || ""}
                onChange={(e) => handleContactChange("address2", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                value={contact?.city || ""}
                onChange={(e) => handleContactChange("city", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={
                  contact.country === "United States" || contact.country === "US" || !contact.country ? usStateList : []
                }
                value={contact.state || ""}
                freeSolo
                onChange={(_, value) => handleContactChange("state", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      autocomplete: "new-password"
                    }}
                    fullWidth
                    label="State"
                    onChange={(e) => handleContactChange("state", e.target.value)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Zip"
                value={contact?.zip || ""}
                onChange={(e) => handleContactChange("zip", e.target.value)}
                error={
                  !!(
                    (contact.country === "US" || contact.country === "United States") &&
                    contact.zip?.trim() &&
                    !isValidUSZip(contact.zip)
                  )
                }
                helperText={
                  (contact.country === "US" || contact.country === "United States") &&
                  contact.zip &&
                  !isValidUSZip(contact.zip)
                    ? "ZIP must be 5 digits or ZIP+4"
                    : ""
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                value={contact?.country || ""}
                options={countryList}
                onChange={(_, value) => handleContactChange("country", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      autocomplete: "new-password"
                    }}
                    fullWidth
                    label="Country"
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}
